/**
 * The Dashboards route — @intrasync/dashboard's `dashboardRoute` adapted to
 * HQ's conventions. HQ carries no fork of the route: everything except the
 * four overrides below (and the header title) is the library's, including
 * `beforeEnter`, which mounts the view store and loads the boards.
 *
 * ONE ROUTE FOR EVERY BOARD. The page is /app/dashboard whichever board is open
 * (the Developer, Sales Manager and Operations Manager boards, and any an admin
 * builds), and the page title never changes with it — the board switcher in the
 * header names the board. So the title is "Dashboards", not the name of the
 * first board the route opened on.
 *
 * WHY EACH OVERRIDE EXISTS:
 *
 * - `requiresPermission: false` — LOAD-BEARING. The library omits the flag, and
 *   @intrasync/stores' `makeRoute` defaults it to TRUE, which makes
 *   PermissionManager demand a GRANT row for `/app/dashboard` in the user's
 *   permissions. HQ only ever writes DENY rows (Page Visibility) and admins do
 *   not bypass the grant check, so as emitted the route would bounce EVERY
 *   user — the CTO included — to `Unauthorized?reason=insufficient_permissions`.
 *   Every HQ route sets it false; the real boundary is the api tier.
 * - `group: "Dashboard & Overview"` — the library's `"Dashboard"` is not in
 *   HQ_NAV_GROUPS, so it would render as its own group after "Management".
 * - `title` / header title "Dashboards" — what the nav and the plate show; the
 *   library's `namespaceTitle("dashboard")` is just "Dashboard", singular.
 *   The rest of the header is the library's on purpose (spread, not restated):
 *   since 0.0.4 it carries `component` — the board switcher and the board's
 *   actions, rendered in HQ's plate beside the title, so the page has ONE
 *   header row on a phone — and `enableRefreshButton: false`, because those
 *   controls bring their own refresh.
 * - `searchAliases` — omnibar tokens; "dashboard" itself matches the title.
 *
 * The cast mirrors `views/routesList.tsx` (`authRoutes as unknown as RouteType[]`):
 * HQ's and the library's RouteTypes are deliberately different shapes.
 */
import { dashboardRoute } from "@intrasync/dashboard";
import type { RouteType } from "types/RouteType";

const libraryRoute = dashboardRoute as unknown as RouteType;

export const DASHBOARDS_TITLE = "Dashboards";

export const dashboardsRoute: RouteType = {
  ...libraryRoute,
  title: DASHBOARDS_TITLE,
  group: "Dashboard & Overview",
  requiresPermission: false,
  searchAliases: ["developer", "board", "dev dashboard"],
  viewportCard: {
    ...libraryRoute.viewportCard,
    header: {
      ...libraryRoute.viewportCard?.header,
      title: DASHBOARDS_TITLE,
    },
  },
};

/** Routes for the dashboard area — spread into routesList.tsx. */
const dashboardRoutesList: RouteType[] = [dashboardsRoute];

export default dashboardRoutesList;
